import {
  UnAuthorized,
  UpgradePlan,
  UpgradePlanTab,
} from '@components/UpgragePlan';
import {
  checkStarterPermission,
  isActionDisabled,
  isActionHidden,
} from '@hooks/url-permission';
import { userPreferencesHook } from '@hooks/user';
import { BrowserUtility } from '@utility/browser-utility';
import { CommonUtility } from '@utility/common';
import { CommonConstant, OrganizationType } from '@utility/constants/common';
import {
  PermissionPageKey,
  PermissionTabKey,
  Permissions,
} from '@utility/constants/permission';
import { DateUtility } from '@utility/date';
import FirebaseService from '@utility/services/firebase';
import UserService from '@utility/services/user';
import { ToastMessage } from '@utility/toast';
import { readOrgFromUrl, writeOrgToUrl } from '@utility/org-url';
import { useNavigate, useLocation } from 'react-router';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFooterActive } from '@hooks/footer-active';
import { placeSwitchEnabled } from '@page-components/auth/unifiedLogin';
import { useQueryClient } from '@tanstack/react-query';
import { UpgradeProPlan, UpgradeProPlanTab } from '@components/UpgrageProPlan';
import {
  PlanConstant,
  planPermissionsToPlanConstant,
} from '@utility/constants/pricing';
import { CacheUtility } from '@utility/cache';
import {
  blocksAdditionalCalls,
  isPublicView,
  redirectFunction,
} from '@page-components/auth/common';
import { LoaderBarFullScreen } from '@components/LoaderBar';
import { useMetadata } from './org-metadata';
import { useSidebar } from './sidebar';
import type {
  IAuthResponse,
  IOrganization,
  IPermissionCategory,
  ISubscription,
  IUser,
} from 'types/auth/types';

interface IAuthContextProps {
  isAuthenticated: boolean | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isOwner: boolean;
  isSupplierOrg: boolean;
  isViewOnly: boolean;
  isStarterPlan: boolean;
  loading: boolean;
  user: IUser | null;
  currentOrg: string | undefined;
  currentOrgInfo: IOrganization | undefined;
  currentRoute: string;
  footerActive: boolean;
  subscription: ISubscription | null;
  preferencesLoading: boolean;
  preferencesProcessing: boolean | string;
  batch:
    | {
        save_paper: string;
      }
    | undefined;
  label:
    | {
        output: string;
      }
    | undefined;
  print:
    | {
        computer: string;
        printer: string;
      }
    | undefined;
  login: (userData: IAuthResponse) => void;
  logout: () => void;
  updateUser: (newData: IUser) => void;
  updatePreferences: (data: any) => Promise<void>;
  switchOrganizationIfDiff: (newOrganizationId: string) => Promise<void>;
  setFooterActive: (active: boolean) => void;
  wmsPortal: boolean;
  redirect: (redirectUrl: string) => void;
  notifications: Array<{
    title: string;
    inApp?: boolean;
    email?: boolean;
  }>;
}

const AuthContext = createContext<IAuthContextProps | undefined>(undefined);

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const currentRoute = location.pathname + location.search;
  const queryClient = useQueryClient();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [user, setUser] = useState<IUser | null>(null);
  const [subscription, setSubscription] = useState<ISubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [publicView, setPublicView] = useState(false);
  const { footerActive, setFooterActive } = useFooterActive();

  const push = (
    path: string | { pathname: string; query?: Record<string, string> },
  ) => {
    if (typeof path === 'string') {
      navigate(path);
    } else {
      const searchParams = path.query
        ? `?${new URLSearchParams(path.query).toString()}`
        : '';
      navigate(`${path.pathname}${searchParams}`);
    }
  };

  const reload = () => {
    window.location.reload();
  };

  const isStarterPlan = useMemo(
    () => subscription?.name === PlanConstant.starter,
    [subscription],
  );

  const blockAdditionalCalls = useMemo(
    () => blocksAdditionalCalls(window.location.pathname, user),
    [user],
  );

  const {
    batch,
    label,
    notifications,
    print,
    update: updatePreferences,
    processing: preferencesProcessing,
    loading: preferencesLoading,
  } = userPreferencesHook(!!isAuthenticated, blockAdditionalCalls);

  const {
    isAdmin,
    isSuperAdmin,
    isOwner,
    isViewOnly,
    currentOrg,
    isSupplierOrg,
    currentOrgInfo,
  } = useMemo(() => {
    const current = (user?.organizations || []).find(
      item => item._id === user?.organization_id,
    );
    return {
      isAdmin: UserService.isAdmin(user),
      isSuperAdmin: UserService.isSuperAdmin(user),
      isViewOnly: user && (user.role === 'user' || user.role === 'view'),
      currentOrg: user?.organization_id,
      isOwner: current?.is_owner,
      isSupplierOrg: current?.type === OrganizationType.supplier,
      currentOrgInfo: current,
    };
  }, [user]);

  const setUserLoginData = (userData: IAuthResponse) => {
    if (window.top === window.self) {
      const user = { ...userData.user };

      setPublicView(isPublicView());
      setUser({ ...user });
      if (userData.subscription) {
        const subscription = { ...(userData.subscription || {}) };
        if (subscription) {
          const expirationDays = DateUtility.difference(
            new Date(),
            new Date(subscription?.expiration_date),
          );
          const expirationSeconds = DateUtility.differenceInSeconds(
            new Date(),
            new Date(subscription?.expiration_date),
          );
          setSubscription({
            ...subscription,
            display_name: subscription.name,
            name: planPermissionsToPlanConstant[
              subscription?.metadata.planPermissions
            ],
            expirationDays,
            expirationSeconds,
          } as ISubscription);
        }
      }
      setIsAuthenticated(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadToken = async () => {
      try {
        setLoading(true);
        const authenticated = UserService.isAuthenticated();
        if (authenticated) {
          let userData = await UserService.refresh();
          const urlOrg = readOrgFromUrl();
          if (urlOrg && urlOrg !== userData?.user?.organization_id) {
            try {
              userData = await UserService.organizationToken({
                organization_id: urlOrg,
              });
            } catch (error) {
              ToastMessage.apiError(error);
            }
          }
          if (userData?.user?.organization_id) {
            BrowserUtility.saveObj(
              CommonConstant.organizationId,
              userData.user.organization_id,
            );
          }
          login(userData);
        } else {
          setLoading(false);
          setIsAuthenticated(false);
        }
      } catch (error) {
        handleLogout();
        setLoading(false);
        setTimeout(() => {
          const query = {
            redirectUrl: `${window.location.pathname}${window.location.search}${window.location.hash}`,
          };
          push({
            pathname: '/login',
            query,
          });
        }, 500);
      }
    };

    if (isPublicView()) {
      if (UserService.isAuthenticated()) {
        setUserLoginData(UserService.getUser());
      } else {
        setLoading(false);
        setIsAuthenticated(false);
      }
    } else {
      loadToken();
    }
  }, []);

  useEffect(() => {
    // Check that a new route is OK
    const handleRouteChange = url => {
      if (!loading) {
        const query = {
          redirectUrl: `${window.location.pathname}${window.location.search}${window.location.hash}`,
        };

        if (!isAuthenticated) {
          if (
            url.indexOf('/login') < 0 &&
            (url.indexOf('/shopify/billing') > -1 || url.indexOf('/app') > -1)
          ) {
            push({
              pathname: '/login',
              query,
            });
          }
        }
      }
    };

    // Check that initial route is OK

    // A user with no organization has to name a workspace first
    if (
      !publicView &&
      isAuthenticated &&
      pathname.indexOf('/app') > -1 &&
      pathname.indexOf('/redirect') === -1 &&
      pathname.indexOf('/app/onboarding') === -1 &&
      !UserService.isSuperAdmin(user) &&
      !CommonUtility.isValidArray(user?.organizations)
    ) {
      push('/app/onboarding');
    } else {
      handleRouteChange(pathname);
    }

    // React Router doesn't have route change events like Next.js
    // The route checks are handled on initial render and location changes
  }, [loading, isAuthenticated, pathname]);

  useEffect(() => {
    const storageChanged = () => {
      const newUser = UserService.getUser();
      if (
        window.location.pathname.indexOf('/login') < 0 &&
        user?.organization_id &&
        newUser?.user?.organization_id !== user?.organization_id
      ) {
        /* Another tab switched organization; reloading under this tab's old
           hash would switch back and bounce the two tabs off each other. */
        if (newUser?.user?.organization_id) {
          writeOrgToUrl(newUser.user.organization_id);
        }
        setTimeout(() => {
          reload();
        }, 100);
      }
    };

    window.addEventListener('storage', storageChanged);
    return () => {
      window.removeEventListener('storage', storageChanged);
    };
  }, [isAuthenticated]);

  const login = (userData: IAuthResponse) => {
    BrowserUtility.remove(CommonConstant.tutorialVideos);
    const temp = {
      ...userData,
      subscription: undefined,
      user: {
        // The Firebase session is checked against this id (firebase.ts).
        _id: userData?.user?._id,
        organization_id: userData?.user?.organization_id,
        email: userData?.user?.email,
      },
    };
    UserService.storeUser(temp);
    if (userData?.user?.organization_id) {
      writeOrgToUrl(userData.user.organization_id);
    }
    setUserLoginData(userData);
  };

  const updateUser = (newData: IUser) => {
    setUser({
      ...user,
      ...newData,
    });
  };

  /* Neither of the stores above is the query cache, and leaving on foot never
     reloads the page, so without this the next account reads the last one's. */
  const handleLogout = () => {
    UserService.logout();
    FirebaseService.logout();
    setIsAuthenticated(false);
    setUser(null);
    CacheUtility.removeAll();
    BrowserUtility.removeAll();
    queryClient.clear();
  };

  const logout = () => {
    handleLogout();
    push('/login');
  };

  const switchOrganizationIfDiff = async newOrganizationId => {
    if (newOrganizationId && newOrganizationId !== currentOrg) {
      const response = placeSwitchEnabled()
        ? await UserService.sessionDivision({ division_id: newOrganizationId })
        : await UserService.organizationToken({
            organization_id: newOrganizationId,
          });
      /* Emptied before the new identity lands, so nothing renders one
         organization's figures under another's name. */
      queryClient.clear();
      login(response);
    }
  };

  const [rejectedUrlOrg, setRejectedUrlOrg] = useState<string | null>(null);
  const switchingToUrlOrg = useRef(false);
  const [reloadingForUrlOrg, setReloadingForUrlOrg] = useState(false);
  const urlOrg = readOrgFromUrl();
  /* Worked out during render so routes are held back in the same pass that
     meets the link, before they can fetch as the organization being left. */
  const urlOrgSwitchPending =
    !!isAuthenticated &&
    !loading &&
    !publicView &&
    !!currentOrg &&
    !!urlOrg &&
    urlOrg !== currentOrg &&
    urlOrg !== rejectedUrlOrg;
  const holdForUrlOrg = urlOrgSwitchPending || reloadingForUrlOrg;

  // biome-ignore lint/correctness/useExhaustiveDependencies: a navigation drops the hash, so it is rechecked on every location change
  useEffect(() => {
    if (!isAuthenticated || loading || publicView || !currentOrg) {
      return;
    }
    if (!urlOrg) {
      writeOrgToUrl(currentOrg);
      return;
    }
    if (!urlOrgSwitchPending || switchingToUrlOrg.current) {
      return;
    }

    switchingToUrlOrg.current = true;
    setReloadingForUrlOrg(true);
    switchOrganizationIfDiff(urlOrg)
      .then(() => {
        /* Providers above the routes, e.g. org metadata, load once per
           session and never learn the organization changed. */
        window.location.reload();
      })
      .catch(error => {
        ToastMessage.apiError(error);
        setReloadingForUrlOrg(false);
        setRejectedUrlOrg(urlOrg);
        writeOrgToUrl(currentOrg);
      })
      .finally(() => {
        switchingToUrlOrg.current = false;
      });
  }, [
    location.key,
    urlOrgSwitchPending,
    currentOrg,
    isAuthenticated,
    loading,
    publicView,
  ]);

  return (
    <AuthContext.Provider
      value={{
        batch,
        currentOrg,
        currentOrgInfo,
        currentRoute,
        footerActive,
        isAdmin,
        isAuthenticated,
        isOwner: !!isOwner,
        isStarterPlan,
        isSuperAdmin,
        isSupplierOrg,
        isViewOnly: !!isViewOnly,
        label,
        loading: loading || holdForUrlOrg,
        login,
        logout,
        notifications,
        preferencesLoading,
        preferencesProcessing,
        print,
        setFooterActive,
        subscription,
        switchOrganizationIfDiff,
        updatePreferences,
        updateUser,
        user,
        wmsPortal: blockAdditionalCalls,
        redirect: redirectUrl =>
          redirectFunction(redirectUrl, subscription, navigate),
      }}
    >
      {(loading || holdForUrlOrg) && <LoaderBarFullScreen />}
      <>{children}</>
    </AuthContext.Provider>
  );
}

export const useAuth = (): IAuthContextProps => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const ProtectRoute = <P extends object>(
  Component: React.ComponentType<P>,
) => {
  const WrappedComponent = (props: P) => {
    const { isAuthenticated, loading, subscription, isSuperAdmin } = useAuth();
    // @ts-expect-error - TS2339: Property 'getMenuByUrl' does not exist on type 'unknown'.
    const { getMenuByUrl, currentMenuPermission } = useSidebar();
    // @ts-expect-error - TS2339: Property 'isSummaryView' does not exist on type 'unknown'.
    const { isSummaryView } = useMetadata();

    if (loading || (!isAuthenticated && window.location.pathname !== '/')) {
      return null;
    }

    const byPassUrls = ['/app/onboarding/', '/app/settings/'];
    const byPassUrl = byPassUrls.includes(window.location.pathname);
    const basePath = decodeURI(window.location.pathname).slice(0, -1);
    const options = { fullMatch: false };
    const menu = getMenuByUrl(basePath, options);

    if (
      (!subscription ||
        !['active', 'trialing', 'past_due'].includes(subscription?.status)) &&
      !byPassUrl &&
      !isSuperAdmin
    ) {
      return <UpgradePlan menu={menu} />;
    }

    if (basePath.indexOf('/app/scm/') > -1) {
      options.fullMatch = true;
    }

    if (
      !byPassUrl &&
      menu?.permission === Permissions.upgrade &&
      !isSuperAdmin
    ) {
      return <UpgradeProPlan menu={menu} />;
    }

    const isExpired = CommonUtility.isSubscriptionExpired(subscription);

    if (basePath === '/app' && isSummaryView && isExpired && !isSuperAdmin) {
      return <UpgradePlan />;
    }

    if (
      !byPassUrl &&
      currentMenuPermission === Permissions.hidden &&
      !isSuperAdmin
    ) {
      return <UnAuthorized />;
    }

    return <Component {...props} />;
  };

  WrappedComponent.displayName = `Protected(${Component.displayName || Component.name})`;

  return WrappedComponent;
};

export const SupplierOrgProtectedRoute = Component =>
  function componentFunction() {
    const { isSupplierOrg } = useAuth();

    if (isSupplierOrg) {
      return null;
    }

    return <Component />;
  };

function isPermissionCategory(
  module: string | IPermissionCategory | undefined,
): module is IPermissionCategory {
  return typeof module !== 'string' && !!module?.settings;
}

export function getTabPermission({ tab }) {
  const { user, loading, subscription, isStarterPlan, isSuperAdmin } =
    useAuth();

  const permission = useMemo(() => {
    switch (tab) {
      case PermissionTabKey.CatalogDiscontinued:
      case PermissionTabKey.CatalogMarketplaces:
      case PermissionTabKey.CatalogQTYRules:
      case PermissionTabKey.CatalogReturnPolicy:
        return user?.permissions?.[PermissionPageKey.Catalog].settings[tab];

      case PermissionTabKey.SPDetails:
      case PermissionTabKey.SPComponents:
      case PermissionTabKey.SPShipments:
      case PermissionTabKey.SPShippingTemplate:
      case PermissionTabKey.SPInventory: {
        const spModule =
          user?.permissions[PermissionPageKey.SCM].modules?.[
            PermissionPageKey.SP
          ];
        if (isPermissionCategory(spModule)) {
          return spModule.settings?.[tab];
        }
        return undefined;
      }
      case PermissionTabKey.OrderCarrierServices:
      case PermissionTabKey.OrderOrderMapping:
        return user?.permissions?.[PermissionPageKey.Orders].settings[tab];

      case PermissionTabKey.SettingsOrganization:
        return (
          user?.role === 'superadmin' ||
          user?.permissions?.[PermissionPageKey.Settings].settings[tab]
        );
      case PermissionTabKey.SettingsUsers:
        return user?.permissions?.[PermissionPageKey.Settings].settings[tab];
      default:
        return '';
    }
  }, [user, tab, loading]);

  const isDisabled = isActionDisabled(permission);

  return {
    permission,
    isAllowed: !isDisabled,
    isDisabled,
    disabled:
      checkStarterPermission(
        permission,
        isStarterPlan && subscription?.trial,
      ) &&
      isActionDisabled(permission) &&
      !isSuperAdmin,
    hidden: isActionHidden(permission),
  };
}

export function ProtectedTab({ tab, children }) {
  const { user, subscription, loading, isSuperAdmin } = useAuth();

  if (loading) {
    return null;
  }

  const permission = useMemo(() => {
    switch (tab) {
      case PermissionTabKey.CatalogDiscontinued:
      case PermissionTabKey.CatalogMarketplaces:
      case PermissionTabKey.CatalogQTYRules:
      case PermissionTabKey.CatalogReturnPolicy:
        return user?.permissions?.[PermissionPageKey.Catalog].settings[tab];

      case PermissionTabKey.SPDetails:
      case PermissionTabKey.SPComponents:
      case PermissionTabKey.SPShipments:
      case PermissionTabKey.SPShippingTemplate: {
        const spModule =
          user?.permissions[PermissionPageKey.SCM].modules?.[
            PermissionPageKey.SP
          ];

        if (isPermissionCategory(spModule)) {
          return spModule.settings?.[tab];
        }

        return undefined;
      }

      case PermissionTabKey.OrderCarrierServices:
      case PermissionTabKey.OrderOrderMapping:
        return user?.permissions?.[PermissionPageKey.Orders]?.settings[tab];

      case PermissionTabKey.SettingsOrganization:
      case PermissionTabKey.SettingsUsers:
        return user?.permissions?.[PermissionPageKey.Settings].settings[tab];

      default:
        return '';
    }
  }, [user, tab]);

  if (
    !subscription?.trial &&
    permission === Permissions.upgrade &&
    !isSuperAdmin
  ) {
    return <UpgradeProPlanTab />;
  }

  if (
    (!subscription ||
      !['active', 'trialing', 'past_due'].includes(subscription?.status)) &&
    !isSuperAdmin
  ) {
    return <UpgradePlanTab />;
  }

  return children;
}

export function isDashboardActionsAllowed() {
  const { isAdmin, user, loading } = useAuth();

  if (loading) return false;

  return isAdmin || user?.role === Permissions.editor;
}
